import { Anchor } from '@repo/ui/src/components/Anchor';
import { ProductVariation, VariationGroup } from '@typings/product';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image';

interface ItemProps {
  variationGroup: VariationGroup.apparelColor | VariationGroup.grillColor;
  color: ProductVariation;
  dark: boolean;
  selected?: boolean;
}

const Item = ({ variationGroup, color, dark, selected = false }: ItemProps) => (
  <p className="tw-flex tw-items-center" aria-current={selected}>
    <span
      className={classNames(
        'tw-flex tw-justify-center tw-items-center tw-mr-2 tw-w-7 tw-h-7 tw-rounded-full tw-bg-[#f6f4f3] tw-border-2 ',
        selected ? 'tw-border-brand-300' : 'tw-border-warm-400',
        !color.isOrderable && 'tw-grayscale',
      )}
    >
      {color.colorVariationImage && (
        <Image
          src={color.colorVariationImage}
          width={20}
          height={20}
          alt=""
          className="tw-rounded-full"
        />
      )}
    </span>
    <span
      className={classNames(
        'tra-text-web-utl-link tw-cursor-pointer hover:tw-text-brand-300',
        dark && color.isOrderable && 'tw-text-white',
        dark && !color.isOrderable && 'tw-text-neutral-200',
        !dark && !color.isOrderable && 'tw-text-neutral-400',
      )}
    >
      {color.attributesMap[variationGroup]}
    </span>
  </p>
);

export interface AlternateColorsProps {
  variationGroup: VariationGroup.apparelColor | VariationGroup.grillColor;
  activeColor: string;
  colors: Array<ProductVariation>;
  dark?: boolean;
  step?: number | `${number}`;
}

const AlternateColors = ({
  variationGroup,
  activeColor,
  colors,
  dark = false,
  step,
}: AlternateColorsProps) => {
  const { t } = useTranslation();
  return (
    <div className={classNames(dark && 'tw-text-white')}>
      <p className={classNames('tra-text-web-utl-link', dark && 'tw-text-white')}>
        {step && `0${step} - `}
        {t('productDetailPageContent.selectColorText', undefined, {
          default: 'Select A Color',
        })}
        :
      </p>
      <p className="sr-only">
        {colors?.length}{' '}
        {t('productDetailPageContent.colorsAvailableText', undefined, {
          default: 'colors available',
        })}
        ,{' '}
        {t('productDetailPageContent.selectedOptionText', undefined, {
          default: 'Selected option is',
        })}{' '}
        {activeColor}
      </p>
      <div className="tw-pt-2 tw-flex tw-align-start tw-gap-6">
        {colors?.map((color) => {
          if (color.attributesMap[variationGroup] !== activeColor) {
            return (
              <Anchor key={color.productUrl} link={color.productUrl}>
                <Item color={color} variationGroup={variationGroup} dark={dark} />
              </Anchor>
            );
          }

          return (
            <Item
              key={color.productUrl}
              selected
              color={color}
              variationGroup={variationGroup}
              dark={dark}
            />
          );
        })}
      </div>
    </div>
  );
};

export default AlternateColors;
